import React, { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from 'react'
import type { ReactNode } from 'react'
import { selectRestoring, selectSessionAttempt, toBootPhase } from './boot-machine'
import type { BootSnapshot, createBootActor } from './boot-machine'
import type { BootEvent, BootPhase } from './boot-phase'

export type BootActor = ReturnType<typeof createBootActor>

export type BootContextValue = {
    phase: BootPhase
    send: (event: BootEvent) => void
    /** Zero for the first session load, then 1, 2, 3 for each transient retry. */
    attempt: number
    /** True while a restored session is validated again. The app stays mounted until boot settles. */
    restoring: boolean
}

const BootContext = createContext<BootContextValue | undefined>(undefined)

/**
 * Delivers the boot actor's current phase. The actor is the store; this only subscribes to it. The
 * caller creates and starts the actor, so the provider never owns its lifetime.
 */
export const BootProvider = ({ actor, children }: { actor: BootActor; children?: ReactNode }) => {
    const subscribe = useCallback(
        (onChange: () => void) => {
            const subscription = actor.subscribe(onChange)
            return () => subscription.unsubscribe()
        },
        [actor],
    )
    const getSnapshot = useCallback((): BootSnapshot => actor.getSnapshot(), [actor])
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

    const value = useMemo<BootContextValue>(
        () => ({
            phase: toBootPhase(snapshot),
            send: (event) => actor.send(event),
            attempt: selectSessionAttempt(snapshot),
            restoring: selectRestoring(snapshot),
        }),
        [actor, snapshot],
    )

    return <BootContext.Provider value={value}>{children}</BootContext.Provider>
}

export const useBoot = (): BootContextValue => {
    const value = useContext(BootContext)
    if (value === undefined) {
        throw new Error('useBoot must be used inside a BootProvider')
    }
    return value
}
